import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { Link, router, useForm } from '@inertiajs/react'
import { useState } from 'react'
import {
  Edit,
  Trash2,
  Users,
  Award,
  Star,
  Calendar,
  Clock,
  Gift,
  UserPlus
} from 'lucide-react'

interface BadgeDetail {
  id: number
  slug: string
  name: string
  description: string | null
  emoji: string
  category: string
  condition_type: string | null
  condition_value: Record<string, unknown>
  points_reward: number
  community_id: number | null
  community_name: string | null
  created_at: string
  updated_at: string
}

interface UserWithBadge {
  id: number
  name: string
  email: string
  specialty: string | null
  earned_at: string
}

interface UserWithoutBadge {
  id: number
  name: string
  email: string
}

interface ShowProps {
  badge: BadgeDetail
  users_with_badge: UserWithBadge[]
  users_without_badge: UserWithoutBadge[]
  total_awarded: number
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function getCategoryBadgeColor(category: string): string {
  switch (category) {
    case 'performance': return 'bg-blue-100 text-blue-800 border-blue-300'
    case 'community': return 'bg-green-100 text-green-800 border-green-300'
    case 'recognition': return 'bg-yellow-100 text-yellow-800 border-yellow-300'
    case 'seniority': return 'bg-purple-100 text-purple-800 border-purple-300'
    default: return 'bg-gray-100 text-gray-800 border-gray-300'
  }
}

function getCategoryLabel(category: string): string {
  switch (category) {
    case 'performance': return 'Performance'
    case 'community': return 'Communauté'
    case 'recognition': return 'Reconnaissance'
    case 'seniority': return 'Ancienneté'
    default: return category
  }
}

function getConditionTypeLabel(conditionType: string | null): string {
  if (!conditionType) return 'Attribution manuelle'

  const labels: Record<string, string> = {
    'posts_count': 'Nombre de publications',
    'comments_count': 'Nombre de commentaires',
    'likes_received': 'Likes reçus',
    'points_total': 'Points totaux',
    'level': 'Niveau atteint',
    'days_active': 'Jours d\'activité',
    'solutions_count': 'Solutions apportées',
    'api_satisfaction': 'API - Satisfaction patient',
    'api_tc_total': 'API - Total TC',
    'api_tc_month': 'API - TC mensuel',
    'api_seniority': 'API - Ancienneté'
  }
  return labels[conditionType] || conditionType
}

export default function Show({ badge, users_with_badge, users_without_badge, total_awarded }: ShowProps) {
  const [showAwardModal, setShowAwardModal] = useState(false)

  const awardForm = useForm({
    user_id: ''
  })

  const handleAwardBadge = (e: React.FormEvent) => {
    e.preventDefault()
    awardForm.post(`/admin/badges/${badge.slug}/award_to_user`, {
      onSuccess: () => {
        setShowAwardModal(false)
        awardForm.reset()
      }
    })
  }

  const handleDelete = () => {
    if (confirm(`Voulez-vous vraiment supprimer le badge "${badge.name}" ?`)) {
      router.delete(`/admin/badges/${badge.slug}`)
    }
  }

  return (
    <AdminLayout
      title={badge.name}
      breadcrumbs={[
        { label: 'Badges', href: '/admin/badges' },
        { label: badge.name }
      ]}
    >
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-tessan-cream flex items-center justify-center text-4xl">
            {badge.emoji}
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-dark">{badge.name}</h2>
            <p className="text-text-gray text-sm">{badge.slug}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getCategoryBadgeColor(badge.category)}`}>
                {getCategoryLabel(badge.category)}
              </span>
              {badge.community_name ? (
                <span className="text-sm text-text-gray">{badge.community_name}</span>
              ) : (
                <span className="text-xs text-text-gray bg-gray-100 px-2 py-1 rounded">Global</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowAwardModal(true)}
            className="flex items-center gap-2"
          >
            <UserPlus size={16} />
            Attribuer
          </Button>
          <Link href={`/admin/badges/${badge.slug}/edit`}>
            <Button variant="secondary" className="flex items-center gap-2">
              <Edit size={16} />
              Modifier
            </Button>
          </Link>
          <Button
            variant="secondary"
            className="flex items-center gap-2 text-red-600 hover:bg-red-50"
            onClick={handleDelete}
          >
            <Trash2 size={16} />
            Supprimer
          </Button>
        </div>
      </div>

      {/* Badge Info */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <h3 className="text-lg font-display font-bold text-text-dark mb-4 flex items-center gap-2">
            <Award size={20} className="text-tessan-primary" />
            Informations
          </h3>

          <div className="space-y-4">
            {badge.description && (
              <div>
                <p className="text-sm text-text-gray">Description</p>
                <p className="text-text-dark">{badge.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-text-gray">Condition d'obtention</p>
                <p className="font-medium text-text-dark">
                  {getConditionTypeLabel(badge.condition_type)}
                </p>
                {badge.condition_type && badge.condition_value && Object.keys(badge.condition_value).length > 0 && (
                  <p className="text-xs text-text-gray mt-1">
                    Seuil: {JSON.stringify(badge.condition_value)}
                  </p>
                )}
              </div>

              <div>
                <p className="text-sm text-text-gray">Points bonus</p>
                <p className="font-medium text-text-dark flex items-center gap-1">
                  <Gift size={16} className="text-green-600" />
                  {badge.points_reward > 0 ? `+${badge.points_reward} pts` : 'Aucun'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-sm text-text-gray flex items-center gap-1">
                  <Calendar size={14} />
                  Créé le
                </p>
                <p className="text-text-dark">{formatDate(badge.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-text-gray flex items-center gap-1">
                  <Clock size={14} />
                  Modifié le
                </p>
                <p className="text-text-dark">{formatDate(badge.updated_at)}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-display font-bold text-text-dark mb-4 flex items-center gap-2">
            <Star size={20} className="text-tessan-primary" />
            Statistiques
          </h3>

          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-tessan-cream flex items-center justify-center mx-auto mb-4">
                <Users size={40} className="text-tessan-primary" />
              </div>
              <p className="text-4xl font-bold text-text-dark">{total_awarded}</p>
              <p className="text-text-gray">utilisateur{total_awarded > 1 ? 's' : ''} possède{total_awarded > 1 ? 'nt' : ''} ce badge</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Users with Badge */}
      <Card className="p-6">
        <h3 className="text-lg font-display font-bold text-text-dark mb-4 flex items-center gap-2">
          <Users size={20} className="text-tessan-primary" />
          Utilisateurs ({total_awarded})
        </h3>

        {users_with_badge.length === 0 ? (
          <div className="text-center py-8 text-text-gray">
            <Award size={32} className="mx-auto mb-2 text-gray-300" />
            <p>Aucun utilisateur n'a encore obtenu ce badge</p>
            <Button
              onClick={() => setShowAwardModal(true)}
              className="mt-4"
            >
              Attribuer le badge
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-text-gray">Utilisateur</th>
                  <th className="text-left px-4 py-3 font-medium text-text-gray">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-text-gray">Spécialité</th>
                  <th className="text-left px-4 py-3 font-medium text-text-gray">Obtenu le</th>
                  <th className="text-right px-4 py-3 font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users_with_badge.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-tessan-primary text-white flex items-center justify-center text-sm font-medium">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-text-dark">{user.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-gray">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-text-dark">{user.specialty || '-'}</td>
                    <td className="px-4 py-3 text-sm text-text-gray">{formatShortDate(user.earned_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link href={`/admin/users/${user.id}`}>
                        <Button variant="secondary" className="p-2">
                          <Edit size={14} />
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Award Badge Modal */}
      {showAwardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-lg font-display font-bold text-text-dark mb-4 flex items-center gap-2">
              <UserPlus size={20} className="text-tessan-primary" />
              Attribuer le badge
            </h3>

            <div className="flex items-center gap-3 p-4 bg-tessan-cream rounded-lg mb-4">
              <span className="text-3xl">{badge.emoji}</span>
              <div>
                <p className="font-medium text-text-dark">{badge.name}</p>
                {badge.points_reward > 0 && (
                  <p className="text-sm text-green-600">+{badge.points_reward} points</p>
                )}
              </div>
            </div>

            <form onSubmit={handleAwardBadge} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  Sélectionner un utilisateur
                </label>
                <Select
                  value={awardForm.data.user_id}
                  onChange={(e) => awardForm.setData('user_id', e.target.value)}
                  required
                >
                  <option value="">Choisir un utilisateur...</option>
                  {users_without_badge.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </Select>
                {users_without_badge.length === 0 && (
                  <p className="text-sm text-text-gray mt-2">
                    Tous les utilisateurs ont déjà ce badge.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAwardModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={awardForm.processing || !awardForm.data.user_id}
                >
                  Attribuer
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </AdminLayout>
  )
}
